import { t } from "@lingui/core/macro";
import { useAuth } from "react-oidc-context";
import { getActionLink, useNeededActionsQuery } from "../../api/teacherApi.ts";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router";
import { useEffect } from "react";
import { useBecomeTeacherStore } from "../../stores/becomeTeacherStore.ts";

type TeacherActionType = "INFO" | "WARNING" | "URGENT";

interface TeacherAction {
  id: string;
  type: TeacherActionType;
  hasLink?: boolean;
}

const getActionStyles = (type: TeacherActionType) => {
  switch (type) {
    case "INFO":
      return {
        container:
          "bg-soluud-primary/10 border-soluud-primary/20 border-l-soluud-primary",
        icon: "text-soluud-primary",
        title: "text-soluud-primary",
      };
    case "WARNING":
      return {
        container:
          "bg-soluud-warning/10 border-soluud-warning/20 border-l-soluud-warning",
        icon: "text-soluud-warning",
        title: "text-soluud-warning",
      };
    case "URGENT":
      return {
        container: "bg-red-50 border-red-200 border-l-red-500",
        icon: "text-red-500",
        title: "text-red-600",
      };
  }
};

const getActionIcon = (type: TeacherActionType) => {
  switch (type) {
    case "INFO":
      return (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "WARNING":
      return (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "URGENT":
      return (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
};

const getActionTypeLabel = (type: TeacherActionType) => {
  switch (type) {
    case "INFO":
      return t`Information`;
    case "WARNING":
      return t`Warning`;
    case "URGENT":
      return t`Urgent`;
  }
};

const messageMap: Record<string, () => string> = {
  "create.location": () =>
    t`You have to create at least one teaching location and one teaching schedule so you can be booked as a teacher.`,
  "create.lesson.prices": () =>
    t`You have to create at least one lesson price so you can be booked as a teacher.`,
  "psp.account.creation-required": () =>
    t`You have to create a fully onboarded stripe account before you can be booked as a teacher and we can pay out your earnings. Click here to be forwarded to stripe.`,
  "psp.account.update-required": () =>
    t`You need to update your stripe account. Not doing this could lead to you not beeing able to be booked and stopped payouts. Click here to be forwarded to stripe.`,
  "psp.account.blocked": () =>
    t`Your payouts and bookings are blocked. Update your stripe account so you can be booked again. Click here to be forwarded to stripe.`,
};

const getRedirectedParam = (searchParams: URLSearchParams) => {
  const isRedirectedParam = searchParams.get("isRedirected");
  if (isRedirectedParam == null) {
    return undefined;
  }
  return Boolean(isRedirectedParam);
};

export const NeededTeacherActions = () => {
  const [searchParams] = useSearchParams();
  const isRedirected = getRedirectedParam(searchParams);
  const { setIsBecomingTeacher } = useBecomeTeacherStore();
  const { data } = useNeededActionsQuery(isRedirected);
  const { user } = useAuth();
  const translateMessage = (actionId: string, fallback?: string) => {
    const translator = messageMap[actionId];
    return translator ? translator() : fallback || actionId;
  };

  useEffect(() => {
    if (isRedirected) {
      setIsBecomingTeacher(true);
    }
  }, [isRedirected, setIsBecomingTeacher]);

  const handleActionClick = async (action: TeacherAction) => {
    if (!action.hasLink) return;
    try {
      const actionLinkResponse = await getActionLink(user!, action.id);
      window.location.href = actionLinkResponse.url;
    } catch (e) {
      console.error(e);
      toast.error(t`Could not forward. Please try again later.`);
    }
  };

  const actions = data?.teacherActions || [];

  const sortedActions = [...actions].sort((a, b) => {
    const priority = { URGENT: 3, WARNING: 2, INFO: 1 };
    return priority[b.type] - priority[a.type];
  });

  return (
    <div className="mb-4 space-y-4">
      {sortedActions.map((action, index) => {
        const styles = getActionStyles(action.type);
        const icon = getActionIcon(action.type);
        const typeLabel = getActionTypeLabel(action.type);
        const message = translateMessage(action.id);
        const hasLink = action.hasLink;
        const actionKey = `${action.id}-${index}`;

        return (
          <div
            key={actionKey}
            onClick={() => handleActionClick(action)}
            className={`rounded-lg border-l-4 p-4 ${styles.container} transition-all duration-200 ${
              hasLink ? "cursor-pointer hover:shadow-md" : ""
            }`}
          >
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${styles.icon} mt-0.5 mr-3`}>
                {icon}
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-center">
                  <span
                    className={`text-xs font-medium tracking-wide uppercase ${styles.title}`}
                  >
                    {typeLabel}
                  </span>
                </div>
                <p className="leading-relaxed text-gray-800">{message}</p>
                {hasLink && (
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center text-sm font-medium ${styles.title} hover:underline`}
                    >
                      <svg
                        className="ml-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
